import { useCallback, useState } from "react";

import { captureException } from "@sentry/react";
import { toast } from "react-hot-toast";

import {
  createMultipartUpload,
  MultipartUploadHandle,
} from "@/client/media-upload";
import {
  BlockAttachmentFragmentDoc,
  useCreateBlockAttachmentMutation,
  useRemoveBlockAttachmentMutation,
} from "@/generated/graphql";

export interface SubmissionFile {
  id: string;
  name: string;
  url: string;
  fileSize: number | null;
}

type OnUploadFile = (
  name: string,
  file: File,
  onProgress?: (pct: number) => void
) => Promise<SubmissionFile | undefined>;
type OnDeleteFile = (attachmentId: string) => Promise<boolean>;

/**
 * Return type for useUploadBlockAttachment hook.
 * Provides upload function and loading state.
 */
type useUploadBlockAttachmentReturn = readonly [
  uploadFile: OnUploadFile,
  isUploading: boolean,
];

/**
 * Return type for useRemoveBlockAttachment hook.
 * Provides delete function and loading state.
 */
type UseDeleteSubmissionFileReturn = readonly [
  deleteFile: OnDeleteFile,
  isDeleting: boolean,
];

/**
 * Hook for uploading files as part of block attachments
 *
 * @param answerBlockId Answer block ID to associate the file with (required)
 * @returns Tuple of [uploadFile, isUploading] where uploadFile is the upload
 * function and isUploading indicates loading state
 */
export function useUploadBlockAttachment(
  answerBlockId: string
): useUploadBlockAttachmentReturn {
  const [fileInFlight, setFileInFlight] = useState(0);
  const [createBlockAttachment] = useCreateBlockAttachmentMutation({
    update(cache, { data }) {
      if (!data?.createBlockAttachment) return;

      const newAttachment = data.createBlockAttachment;
      const answerBlockCacheId = `AnswerBlock:${answerBlockId}`;

      // Update the AnswerBlock's blockAttachments array
      cache.modify({
        id: answerBlockCacheId,
        fields: {
          blockAttachments(existingAttachments = []) {
            const newAttachmentRef = cache.writeFragment({
              data: newAttachment,
              fragment: BlockAttachmentFragmentDoc,
              fragmentName: "BlockAttachment",
            });
            return [...existingAttachments, newAttachmentRef];
          },
        },
      });
    },
  });

  const callback = useCallback<OnUploadFile>(
    async (name, file, onProgress) => {
      // to keep track of concurrent uploads
      setFileInFlight((n) => n + 1);
      let upload: MultipartUploadHandle | undefined;
      try {
        upload = createMultipartUpload(file, { onProgress });
        const media = await upload.start();

        // Create block attachment with the uploaded media
        // The cache update happens automatically via the update function above
        const result = await createBlockAttachment({
          variables: {
            answerBlockId,
            mediaId: media.id,
            mediaUrl: media.url,
          },
        });

        const blockAttachment = result.data?.createBlockAttachment;
        if (!blockAttachment) {
          throw new Error("Failed to create block attachment");
        }

        toast.success(`File "${name}" uploaded successfully`);
        return {
          id: blockAttachment.id,
          name: blockAttachment.name,
          url: blockAttachment.mediaUrl ?? media.url,
          fileSize: blockAttachment.fileSize,
        };
      } catch (err) {
        // No resume UI here, so abort instead of keeping the upload for
        // resume — leaves no orphaned multipart parts in S3.
        upload?.cancel();
        toast.error(
          "Upload failed. Check your internet connection and try again."
        );
        captureException(err, {
          extra: { answerBlockId, fileName: name, fileSize: file.size },
        });
      } finally {
        // createBlockAttachment can throw after a successful upload —
        // decrement once.
        setFileInFlight((n) => n - 1);
      }
      return undefined;
    },
    [answerBlockId, setFileInFlight, createBlockAttachment]
  );

  return [callback, fileInFlight > 0];
}

/**
 * Hook for deleting files as part of work submission using GraphQL mutation.
 *
 * @returns Tuple of [deleteFile, isDeleting] where deleteFile is the delete
 * function and isDeleting indicates loading state
 */
export function useRemoveBlockAttachment(): UseDeleteSubmissionFileReturn {
  const [removeBlockAttachment, { loading }] = useRemoveBlockAttachmentMutation(
    {
      update(cache, { data }) {
        if (!data?.removeBlockAttachment) return;

        const removedAttachment = data.removeBlockAttachment;

        // The attachment is automatically removed from the cache,
        // but we need to evict it from the AnswerBlock's blockAttachments array
        cache.evict({
          id: cache.identify(removedAttachment),
        });

        // Clean up any dangling references
        cache.gc();
      },
    }
  );

  const callback = useCallback<OnDeleteFile>(
    async (attachmentId) => {
      try {
        const result = await removeBlockAttachment({
          variables: {
            blockAttachmentId: attachmentId,
          },
        });

        if (result.data?.removeBlockAttachment) {
          toast.success("File deleted successfully");
          return true;
        } else {
          toast.error("Delete failed. Please try again.");
          return false;
        }
      } catch (error) {
        toast.error(
          "Delete failed. Check your internet connection and try again."
        );
        console.error("Submission file delete error:", error);
        return false;
      }
    },
    [removeBlockAttachment]
  );

  return [callback, loading];
}
